import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications, useStore } from '../store/useStore';
import { useAuth } from './useAuth';
import {
    SUBSCRIPTION_DRAFTS_CHANGED_EVENT,
    SUBSCRIPTION_DRAFTS_PATH,
    fetchSubscriptionDraftInvoices,
} from '../lib/subscriptionInvoiceUtils';
import { oggiRoma } from '../lib/dataItalia';
import { APP_ICON } from '../lib/brand';

const AUDIO_PATH = '/sounds/reminder.mp3';
// Le bozze nascono di notte: controllare ogni 5 minuti basta e avanza.
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const LAST_ALERT_STORAGE_KEY = 'pixel-slab-subscription-drafts-alert';
const NOTIFICATION_ID = 'subscription-drafts';

/** Ruoli che vedono la pagina Fatture: agli altri l'avviso non servirebbe. */
export const INVOICE_ROLES: readonly string[] = ['admin', 'commerciale', 'amministrazione'];

/** Ultimo avviso dato in questo browser: giorno e numero di bozze. */
interface LastAlert {
    day: string;
    count: number;
}

const loadLastAlert = (): LastAlert | null => {
    try {
        const raw = window.localStorage.getItem(LAST_ALERT_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as Partial<LastAlert>;
        return typeof parsed.day === 'string' && typeof parsed.count === 'number'
            ? { day: parsed.day, count: parsed.count }
            : null;
    } catch {
        return null;
    }
};

const saveLastAlert = (alert: LastAlert) => {
    try {
        window.localStorage.setItem(LAST_ALERT_STORAGE_KEY, JSON.stringify(alert));
    } catch {
        /* storage non disponibile: si suonerà a ogni apertura, non è grave */
    }
};

const draftsMessage = (count: number): string =>
    count === 1
        ? '1 bozza di abbonamento da controllare ed emettere'
        : `${count} bozze di abbonamento da controllare ed emettere`;

// Avviso del mattino per le bozze preparate dal giro notturno: pallino sulla
// voce Fatture, suono, notifica del browser e avviso in app.
//
// Suona la prima volta che l'app trova bozze nella giornata e ogni volta che
// ne arrivano di nuove. Riaprire l'app con le stesse bozze non risuona.
// Funziona solo con l'app aperta: il canale sicuro sono il banner in Dashboard
// e il pallino, che si vedono comunque all'apertura.
export const useSubscriptionDraftAlerts = () => {
    const { addNotification, removeNotification } = useNotifications();
    const setSubscriptionDraftCount = useStore((s) => s.setSubscriptionDraftCount);
    const navigate = useNavigate();
    const { profile } = useAuth();
    const canSeeInvoices = !!profile?.role && INVOICE_ROLES.includes(profile.role);

    const audioRef = useRef<HTMLAudioElement | null>(null);
    // true quando il browser ha bloccato il suono: parte al primo gesto.
    const pendingSoundRef = useRef(false);

    useEffect(() => {
        const audio = new Audio(AUDIO_PATH);
        audio.preload = 'auto';
        audioRef.current = audio;
    }, []);

    const playSound = useCallback(() => {
        const audio = audioRef.current;
        if (!audio) return;
        audio.currentTime = 0;
        audio.play()
            .then(() => { pendingSoundRef.current = false; })
            .catch(() => { pendingSoundRef.current = true; });
    }, []);

    // Il browser non lascia suonare una pagina finché l'utente non ha cliccato
    // o premuto un tasto. Al mattino l'app spesso si apre e basta: il suono
    // bloccato parte al primo clic.
    useEffect(() => {
        const onGesture = () => {
            if (pendingSoundRef.current) playSound();
        };
        window.addEventListener('pointerdown', onGesture);
        window.addEventListener('keydown', onGesture);
        return () => {
            window.removeEventListener('pointerdown', onGesture);
            window.removeEventListener('keydown', onGesture);
        };
    }, [playSound]);

    useEffect(() => {
        if (!canSeeInvoices) {
            setSubscriptionDraftCount(0);
            return;
        }
        let cancelled = false;

        const openDrafts = () => {
            removeNotification(NOTIFICATION_ID);
            navigate(SUBSCRIPTION_DRAFTS_PATH);
        };

        const fire = (count: number) => {
            const message = draftsMessage(count);
            playSound();
            if ('Notification' in window && Notification.permission === 'granted') {
                const osNotif = new Notification('Fatture da emettere', {
                    body: message,
                    icon: APP_ICON,
                    tag: NOTIFICATION_ID,
                });
                osNotif.onclick = () => {
                    window.focus();
                    openDrafts();
                    osNotif.close();
                };
            }
            // Un solo avviso in app, aggiornato: non uno nuovo per ogni controllo.
            removeNotification(NOTIFICATION_ID);
            addNotification({
                id: NOTIFICATION_ID,
                type: 'warning',
                title: 'Fatture da emettere',
                message,
                persistent: true,
                action: { label: 'Apri bozze', onClick: openDrafts },
            });
        };

        const check = async () => {
            try {
                const drafts = await fetchSubscriptionDraftInvoices();
                if (cancelled) return;
                const count = drafts.length;
                setSubscriptionDraftCount(count);

                if (count === 0) removeNotification(NOTIFICATION_ID);

                const today = oggiRoma();
                const last = loadLastAlert();
                const isFirstToday = !last || last.day !== today;
                if (count > 0 && (isFirstToday || count > (last?.count ?? 0))) fire(count);
                // Si salva anche quando il numero scende, così una bozza nuova
                // arrivata dopo (anche lo stesso giorno) fa suonare di nuovo.
                saveLastAlert({ day: today, count });
            } catch {
                /* non bloccante: il pallino resta com'era fino al prossimo controllo */
            }
        };

        check();
        const intervalId = window.setInterval(check, POLL_INTERVAL_MS);
        // Dopo un'emissione o una bozza nuova il pallino si aggiorna subito,
        // senza aspettare il prossimo giro.
        window.addEventListener(SUBSCRIPTION_DRAFTS_CHANGED_EVENT, check);
        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
            window.removeEventListener(SUBSCRIPTION_DRAFTS_CHANGED_EVENT, check);
        };
    }, [canSeeInvoices, addNotification, removeNotification, navigate, playSound, setSubscriptionDraftCount]);
};

export default useSubscriptionDraftAlerts;
